import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../api/PurchaseAPI/lostOrDamagedItemsAPI';

const getErrorMessage = (err, fallback) =>
    err?.ErrorDetails?.ErrorMessage || err?.Message || err?.message || fallback;

// ── Async Thunks ─────────────────────────────────────────────────────────────

export const fetchCostCenters = createAsyncThunk(
    'lostOrDamagedItems/fetchCostCenters',
    async ({ uid, rid }, { rejectWithValue }) => {
        try {
            const res = await api.getCentralCostCenterMenu(uid, rid);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load cost centers'));
        }
    }
);

export const fetchCCType = createAsyncThunk(
    'lostOrDamagedItems/fetchCCType',
    async (CCType, { rejectWithValue }) => {
        try {
            const res = await api.getCostCenterType(CCType);
            const data = res?.Data;
            const record = Array.isArray(data) ? data[0] : data;
            return record?.CCType || '';
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load cost center type'));
        }
    }
);

export const fetchCategories = createAsyncThunk(
    'lostOrDamagedItems/fetchCategories',
    async (CType, { rejectWithValue }) => {
        try {
            const res = await api.getCategories(CType);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load categories'));
        }
    }
);

export const fetchMainGrid = createAsyncThunk(
    'lostOrDamagedItems/fetchMainGrid',
    async ({ ccode }, { rejectWithValue }) => {
        try {
            const res = await api.getLostOrDamageItem(ccode);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load stock items'));
        }
    }
);

export const fetchSuggestions = createAsyncThunk(
    'lostOrDamagedItems/fetchSuggestions',
    async ({ pfx, typ, cat, cc }, { rejectWithValue }) => {
        try {
            const res = await api.getAutoCompleteItemTransfer(pfx, typ, cat, cc);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load suggestions'));
        }
    }
);

export const fetchLotDetails = createAsyncThunk(
    'lostOrDamagedItems/fetchLotDetails',
    async ({ itemcode, cccode }, { rejectWithValue }) => {
        try {
            const res = await api.getNewLDItemsIssueTempDetails(itemcode, cccode);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load item lot details'));
        }
    }
);

// Success format is "Submited,<ItemCode>".
export const addTempLDItems = createAsyncThunk(
    'lostOrDamagedItems/addTempLDItems',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await api.saveTempNewItemsLD(payload);
            const raw = res?.Data || '';
            const [status, code] = raw.split(',');
            if (status !== 'Submited') {
                return rejectWithValue(raw || 'Failed to add items');
            }
            return { code };
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to add items'));
        }
    }
);

export const deleteLDItem = createAsyncThunk(
    'lostOrDamagedItems/deleteLDItem',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await api.deleteLDItems(payload);
            const status = res?.Data || '';
            if (status !== 'Submited') {
                return rejectWithValue(status || 'Failed to delete item');
            }
            return true;
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to delete item'));
        }
    }
);

// Success format is "Submited,<RefNo/Ext>" — the second token doubles as the S3 upload key.
export const submitLostOrDamaged = createAsyncThunk(
    'lostOrDamagedItems/submit',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await api.insertLostorDamageMultipleItem(payload);
            const raw = res?.Data || '';
            const [status, code] = raw.split(',');
            if (status !== 'Submited') {
                return rejectWithValue(raw || 'Failed to submit');
            }
            return { code };
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to submit'));
        }
    }
);

// ── Slice ────────────────────────────────────────────────────────────────────

const initialState = {
    ccList: [],
    ccType: null,
    categoryList: [],
    mainGridItems: [],
    suggestions: [],
    lotDetails: [],
    addTempResult: null,
    submitResult: null,
    loading: {
        ccList: false,
        ccType: false,
        categoryList: false,
        mainGrid: false,
        suggestions: false,
        lotDetails: false,
        addTemp: false,
        deleteItem: false,
        submit: false,
    },
    errors: {
        ccList: null,
        ccType: null,
        categoryList: null,
        mainGrid: null,
        lotDetails: null,
        addTemp: null,
        deleteItem: null,
        submit: null,
    },
};

const lostOrDamagedItemsSlice = createSlice({
    name: 'lostOrDamagedItems',
    initialState,
    reducers: {
        clearCategories: (s) => { s.categoryList = []; },
        clearMainGrid:    (s) => { s.mainGridItems = []; s.errors.mainGrid = null; },
        clearSuggestions: (s) => { s.suggestions = []; },
        clearLotDetails:  (s) => { s.lotDetails = []; s.errors.lotDetails = null; },
        clearAddTempResult: (s) => { s.addTempResult = null; s.errors.addTemp = null; },
        clearSubmitResult:  (s) => { s.submitResult = null; s.errors.submit = null; },
        resetLostOrDamagedItems: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCostCenters.pending,   (s) => { s.loading.ccList = true; s.errors.ccList = null; })
            .addCase(fetchCostCenters.fulfilled, (s, a) => { s.loading.ccList = false; s.ccList = a.payload; })
            .addCase(fetchCostCenters.rejected,  (s, a) => { s.loading.ccList = false; s.errors.ccList = a.payload; });

        builder
            .addCase(fetchCCType.pending,   (s) => { s.loading.ccType = true; s.errors.ccType = null; s.ccType = null; })
            .addCase(fetchCCType.fulfilled, (s, a) => { s.loading.ccType = false; s.ccType = a.payload; })
            .addCase(fetchCCType.rejected,  (s, a) => { s.loading.ccType = false; s.errors.ccType = a.payload; });

        builder
            .addCase(fetchCategories.pending,   (s) => { s.loading.categoryList = true; s.errors.categoryList = null; })
            .addCase(fetchCategories.fulfilled, (s, a) => { s.loading.categoryList = false; s.categoryList = a.payload; })
            .addCase(fetchCategories.rejected,  (s, a) => { s.loading.categoryList = false; s.errors.categoryList = a.payload; });

        builder
            .addCase(fetchMainGrid.pending,   (s) => { s.loading.mainGrid = true; s.errors.mainGrid = null; })
            .addCase(fetchMainGrid.fulfilled, (s, a) => { s.loading.mainGrid = false; s.mainGridItems = a.payload; })
            .addCase(fetchMainGrid.rejected,  (s, a) => { s.loading.mainGrid = false; s.errors.mainGrid = a.payload; s.mainGridItems = []; });

        builder
            .addCase(fetchSuggestions.pending,   (s) => { s.loading.suggestions = true; })
            .addCase(fetchSuggestions.fulfilled, (s, a) => { s.loading.suggestions = false; s.suggestions = a.payload; })
            .addCase(fetchSuggestions.rejected,  (s) => { s.loading.suggestions = false; s.suggestions = []; });

        builder
            .addCase(fetchLotDetails.pending,   (s) => { s.loading.lotDetails = true; s.errors.lotDetails = null; })
            .addCase(fetchLotDetails.fulfilled, (s, a) => { s.loading.lotDetails = false; s.lotDetails = a.payload; })
            .addCase(fetchLotDetails.rejected,  (s, a) => { s.loading.lotDetails = false; s.errors.lotDetails = a.payload; s.lotDetails = []; });

        builder
            .addCase(addTempLDItems.pending,   (s) => { s.loading.addTemp = true; s.errors.addTemp = null; s.addTempResult = null; })
            .addCase(addTempLDItems.fulfilled, (s, a) => { s.loading.addTemp = false; s.addTempResult = a.payload; s.lotDetails = []; })
            .addCase(addTempLDItems.rejected,  (s, a) => { s.loading.addTemp = false; s.errors.addTemp = a.payload; });

        builder
            .addCase(deleteLDItem.pending,   (s) => { s.loading.deleteItem = true; s.errors.deleteItem = null; })
            .addCase(deleteLDItem.fulfilled, (s) => { s.loading.deleteItem = false; })
            .addCase(deleteLDItem.rejected,  (s, a) => { s.loading.deleteItem = false; s.errors.deleteItem = a.payload; });

        builder
            .addCase(submitLostOrDamaged.pending,   (s) => { s.loading.submit = true; s.errors.submit = null; s.submitResult = null; })
            .addCase(submitLostOrDamaged.fulfilled, (s, a) => { s.loading.submit = false; s.submitResult = a.payload; s.mainGridItems = []; })
            .addCase(submitLostOrDamaged.rejected,  (s, a) => { s.loading.submit = false; s.errors.submit = a.payload; });
    },
});

export const {
    clearCategories,
    clearMainGrid,
    clearSuggestions,
    clearLotDetails,
    clearAddTempResult,
    clearSubmitResult,
    resetLostOrDamagedItems,
} = lostOrDamagedItemsSlice.actions;

// ── Selectors ────────────────────────────────────────────────────────────────
export const selectLDCCList        = (s) => s.lostOrDamagedItems.ccList;
export const selectLDCCType        = (s) => s.lostOrDamagedItems.ccType;
export const selectLDCategoryList  = (s) => s.lostOrDamagedItems.categoryList;
export const selectLDMainGridItems = (s) => s.lostOrDamagedItems.mainGridItems;
export const selectLDSuggestions   = (s) => s.lostOrDamagedItems.suggestions;
export const selectLDLotDetails    = (s) => s.lostOrDamagedItems.lotDetails;
export const selectLDAddTempResult = (s) => s.lostOrDamagedItems.addTempResult;
export const selectLDSubmitResult  = (s) => s.lostOrDamagedItems.submitResult;
export const selectLDLoading       = (s) => s.lostOrDamagedItems.loading;
export const selectLDErrors        = (s) => s.lostOrDamagedItems.errors;

export default lostOrDamagedItemsSlice.reducer;
